import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CryptoService } from '@core/services/encryption/crypto.service';
import { OrganizationService } from '@core/services/organization/organization.service';
import { UserPreferenceService } from '@core/services/user-preference/user-preference.service';
import { take } from 'rxjs';

@Component({
  selector: 'app-vault-setup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './vault.component.html',
  styleUrl: './vault.component.scss',
})
export class VaultSetupComponent implements OnInit {
  @Output() close = new EventEmitter<void>();

  step: 'INIT' | 'MNEMONIC' | 'IMPORT' | 'PIN' | 'DONE' = 'INIT';
  mnemonicWords: string[] = [];
  mnemonic: string = '';

  // Import State
  importMnemonic: string[] = new Array(24).fill('');
  importError: string | null = null;

  hasSaved: boolean = false;

  pin: string = '';
  confirmPin: string = '';
  pinError: string | null = null;
  loading: boolean = false;

  // Organization Verification State
  isFirstTimeSetup: boolean = false; // Default false until checked
  isCheckingOrg: boolean = true;
  existingOrgTag: string | null = null;

  constructor(
    private cryptoService: CryptoService,
    private organizationService: OrganizationService,
    private userPreferenceService: UserPreferenceService,
  ) {}

  ngOnInit() {
    this.checkOrganizationStatus();
  }

  checkOrganizationStatus() {
    this.isCheckingOrg = true;
    this.organizationService.getVaultVerifyTag().subscribe({
      next: (res) => {
        if (res && res.tag) {
          this.existingOrgTag = res.tag;
          this.isFirstTimeSetup = false;
        } else {
          this.isFirstTimeSetup = true;
        }
        this.isCheckingOrg = false;
      },
      error: () => {
        // Assume first time setup on 404 or any error (safer default for fresh orgs)
        this.isFirstTimeSetup = true;
        this.isCheckingOrg = false;
      },
    });
  }

  closeModal() {
    this.close.emit();
  }

  async generate() {
    this.mnemonic = await this.cryptoService.generateMnemonic();
    this.mnemonicWords = this.mnemonic.split(' ');
    this.step = 'MNEMONIC';
  }

  downloadRecoveryPhrase() {
    if (!this.mnemonic) return;

    // Get current org and branch names for the filename
    let orgName: string | undefined;
    let branchName: string | undefined;

    this.userPreferenceService.selectedOrg$.pipe(take(1)).subscribe(org => {
      orgName = org?.name;
    });
    this.userPreferenceService.selectedBranch$.pipe(take(1)).subscribe(branch => {
      branchName = branch?.name;
    });

    const parts = ['medease-recovery-phrase'];
    if (orgName) parts.push(this.sanitizeFilename(orgName));
    if (branchName) parts.push(this.sanitizeFilename(branchName));
    const filename = parts.join('-') + '.txt';

    const blob = new Blob([this.mnemonic], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private sanitizeFilename(name: string): string {
    return name.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-');
  }

  startImport() {
    this.importMnemonic = new Array(24).fill('');
    this.importError = null;
    this.step = 'IMPORT';
  }

  async validateAndImport() {
    this.importError = null;
    const filledWords = this.importMnemonic.map((w) => w.trim().toLowerCase());

    if (filledWords.some((w) => !w)) {
      this.importError = 'Please fill in all 24 words.';
      return;
    }

    const mnemonic = filledWords.join(' ');
    if (mnemonic.split(' ').length !== 24) {
      this.importError = 'Invalid mnemonic format.';
      return;
    }

    this.loading = true;
    try {
      await this.cryptoService.verifyMnemonic(mnemonic);

      if (!this.isFirstTimeSetup) {
        if (!this.existingOrgTag) {
          this.importError =
            'Organization configuration error. Please refresh.';
          this.loading = false;
          return;
        }

        const isValid = await this.cryptoService.verifyExistingTag(
          mnemonic,
          this.existingOrgTag,
        );

        if (!isValid) {
          this.importError =
            "Invalid Recovery Phrase: These words do not match your Organization's vault.";
          this.loading = false;
          return;
        }
      }

      this.mnemonic = mnemonic;
      this.mnemonicWords = filledWords;
      this.step = 'PIN';
    } catch (e) {
      this.importError = 'Invalid recovery phrase. Please check your words.';
    } finally {
      this.loading = false;
    }
  }

  handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData?.getData('text');

    if (pastedText) {
      const words = pastedText
        .trim()
        .split(/\s+/)
        .map((w: string) => w.toLowerCase())
        .filter((w: string) => w.length > 0);

      if (words.length === 24) {
        this.importMnemonic = words;
        this.importError = null;
      } else {
        this.importError = `Expected 24 words, found ${words.length}.`;
      }
    }
  }

  trackByFn(index: number, item: any): number {
    return index;
  }

  async setup() {
    this.pinError = null;
    if (this.pin !== this.confirmPin) {
      this.pinError = 'PINs do not match';
      return;
    }
    if (this.pin.length !== 6 || !/^\d+$/.test(this.pin)) {
      this.pinError = 'PIN must be 6 digits';
      return;
    }

    this.loading = true;
    try {
      if (this.isFirstTimeSetup) {
        // First Time: Create Verification Tag -> Post to API -> Setup Vault
        const verificationTag = await this.cryptoService.createVerificationTag(
          this.mnemonic,
        );

        // Post to API
        this.organizationService.setVaultVerifyTag(verificationTag).subscribe({
          next: async () => {
            // Organization verified, now perform local setup
            await this.localVaultSetup();
          },
          error: (err) => {
            this.loading = false;
            this.pinError =
              'Failed to verify organization setup. Please try again.';
            console.error('Org Setup Failed', err);
          },
        });
      } else {
        // Existing Vault: Verification already done in IMPORT step
        await this.localVaultSetup();
      }
    } catch (e) {
      console.error(e);
      this.pinError = 'Setup failed. Please try again.';
      this.loading = false;
    }
  }

  private async localVaultSetup() {
    try {
      await this.cryptoService.setupVault(this.mnemonic, this.pin);
      this.step = 'DONE';
    } catch (e) {
      console.error(e);
      this.pinError = 'Local storage failed. Please try again.';
    } finally {
      this.loading = false;
    }
  }
}
